import type { TranslationKey } from './index';

/**
 * Bengali (বাংলা). Typed as `Record<TranslationKey, string>` on purpose: adding a
 * key to `en.ts` without one here is a compile error, which is the only reliable
 * way to keep a second language from quietly rotting.
 *
 * Terminology follows the Bangladeshi board-exam vocabulary teachers already use
 * (সৃজনশীল for creative/written, নৈর্ব্যক্তিক for MCQ, অধ্যায় for chapter) rather
 * than literal translations of the English.
 */
export const bn: Record<TranslationKey, string> = {
  // ─── Shared ────────────────────────────────────────────────────────────────
  'common.appName': 'এআই প্রশ্ন জেনারেটর',
  'common.appTagline': 'বাংলাদেশ এসএসসি / এইচএসসি কারিকুলাম',
  'common.brand': 'টিউটর এআই',
  'common.back': 'ফিরে যান',
  'common.dashboard': 'ড্যাশবোর্ড',
  'common.previous': 'পূর্ববর্তী',
  'common.continue': 'পরবর্তী',
  'common.loading': 'লোড হচ্ছে…',
  'common.processing': 'প্রক্রিয়াধীন…',
  'common.cancel': 'বাতিল',
  'common.confirm': 'নিশ্চিত করুন',
  'common.edit': 'সম্পাদনা',
  'common.done': 'সম্পন্ন',
  'common.view': 'দেখুন',
  'common.prev': 'পূর্ববর্তী',
  'common.next': 'পরবর্তী',
  'common.signIn': 'সাইন ইন',
  'common.signOut': 'সাইন আউট',
  'common.expired': 'মেয়াদোত্তীর্ণ',
  'common.written': 'সৃজনশীল',
  'common.mcq': 'নৈর্ব্যক্তিক',
  'common.questions': 'প্রশ্ন',
  'common.minutesShort': 'মিনিট',
  'common.emptyValue': '—',

  // ─── Language toggle ───────────────────────────────────────────────────────
  'language.switchTo': '{language}-এ পরিবর্তন করুন',

  // ─── Departments ───────────────────────────────────────────────────────────
  'dept.science': 'বিজ্ঞান',
  'dept.commerce': 'বাণিজ্য',
  'dept.arts': 'মানবিক',

  // ─── Statuses ──────────────────────────────────────────────────────────────
  'status.pending': 'অপেক্ষমাণ',
  'status.completed': 'সম্পন্ন',
  'status.failed': 'ব্যর্থ',
  'status.cancelled': 'বাতিল',
  'status.active': 'সক্রিয়',
  'status.expired': 'মেয়াদোত্তীর্ণ',

  // ─── Login ─────────────────────────────────────────────────────────────────
  'login.title': 'প্রশ্নপত্র জেনারেটর',
  'login.subtitle': 'এআই-চালিত এসএসসি ও এইচএসসি প্রশ্নপত্র জেনারেটর',
  'login.welcome': 'স্বাগতম',
  'login.description': 'প্রশ্নপত্র তৈরি করতে এবং সাবস্ক্রিপশন পরিচালনা করতে সাইন ইন করুন।',
  'login.google': 'গুগল দিয়ে চালিয়ে যান',
  'login.terms':
    'সাইন ইন করার মাধ্যমে আপনি আমাদের সেবার শর্তাবলিতে সম্মত হচ্ছেন। নতুন ব্যবহারকারীরা ১৪ দিনের ফ্রি ট্রায়াল পাবেন।',

  // ─── Session expiry ────────────────────────────────────────────────────────
  'session.title': 'আপনার সেশনের মেয়াদ শেষ হয়েছে',
  'session.description':
    'নিরাপত্তার জন্য আপনাকে সাইন আউট করা হয়েছে। চালিয়ে যেতে আবার লগ ইন করুন — যা এখনো তৈরি করেননি তা পুনরায় করতে হবে।',
  'session.logout': 'লগ আউট',

  // ─── Dashboard navigation ──────────────────────────────────────────────────
  'nav.analytics': 'বিশ্লেষণ',
  'nav.content': 'কনটেন্ট',
  'nav.account': 'অ্যাকাউন্ট',
  'nav.overview': 'সংক্ষিপ্ত বিবরণ',
  'nav.history': 'ইতিহাস',
  'nav.subscription': 'সাবস্ক্রিপশন',
  'nav.payments': 'পেমেন্ট ইতিহাস',
  'nav.leaderboard': 'লিডারবোর্ড',
  'nav.collapse': 'প্যানেল সংকুচিত করুন',
  'nav.resize': 'প্যানেলের আকার পরিবর্তন — অ্যারো কী ব্যবহার করুন',
  'nav.dragResize': 'আকার পরিবর্তনে টেনে আনুন',
  'nav.breadcrumb': 'হোম / ড্যাশবোর্ড',

  // ─── Dashboard: hero + stats ───────────────────────────────────────────────
  'dashboard.newGeneration': 'নতুন প্রশ্নপত্র',
  'dashboard.heroTitle': 'বোর্ড পরীক্ষার প্রশ্ন তৈরি করুন মুহূর্তেই 🎓',
  'dashboard.heroBody':
    'এআই দিয়ে এসএসসি / এইচএসসি প্রশ্নপত্র তৈরি করুন। বিষয়, অধ্যায় ও কঠিনতা বাছুন — কয়েক সেকেন্ডেই সম্পন্ন।',
  'dashboard.heroCta': 'তৈরি করা শুরু করুন',
  'dashboard.statTotalQuestions': 'মোট প্রশ্ন',
  'dashboard.statThisMonth': 'এই মাসে {count}টি তৈরি হয়েছে',
  'dashboard.statRank': 'লিডারবোর্ড র‍্যাঙ্ক',
  'dashboard.statRankSub': '{count} জন ব্যবহারকারীর মধ্যে',

  // ─── Dashboard: analytics ──────────────────────────────────────────────────
  'dashboard.analyticsTitle': 'বিষয়ভিত্তিক প্রশ্ন',
  'dashboard.analyticsCount': '{count}টি বিষয়',
  'dashboard.analyticsEmptyTitle': 'এখনো কোনো তথ্য নেই',
  'dashboard.analyticsEmptyBody': 'বিশ্লেষণ দেখতে প্রশ্ন তৈরি করা শুরু করুন',
  'dashboard.bySubject': 'বিষয় অনুযায়ী',
  'dashboard.noQuestionsYet': 'এখনো কোনো প্রশ্ন তৈরি করা হয়নি।',
  'dashboard.topUsers': 'শীর্ষ ব্যবহারকারী',
  'dashboard.seeAll': 'সব দেখুন →',
  'dashboard.recentTitle': 'সাম্প্রতিক প্রশ্নপত্র',
  'dashboard.recentViewAll': 'সব দেখুন ({count}) →',
  'dashboard.rankOf': '{total} জনের মধ্যে র‍্যাঙ্ক #{rank}',
  'dashboard.rankUp': 'র‍্যাঙ্ক বাড়াতে প্রশ্ন তৈরি করুন!',
  'dashboard.keepGenerating': 'চালিয়ে যান!',

  // ─── Dashboard: history ────────────────────────────────────────────────────
  'dashboard.historyEmpty': 'এখনো কোনো ইতিহাস নেই। আপনার প্রথম প্রশ্নপত্র তৈরি করুন!',
  'dashboard.questionsCount': '{count}টি প্রশ্ন',

  // ─── Dashboard: subscription tab ───────────────────────────────────────────
  'subscription.signInPrompt': 'সাবস্ক্রিপশন পরিচালনা করতে সাইন ইন করুন।',
  'subscription.loadError': 'সাবস্ক্রিপশন লোড করা যায়নি।',
  'subscription.planLabel': '{plan} প্ল্যান',
  'subscription.daysLeft': '{days} দিন বাকি',
  'subscription.daysShort': '{days} দিন',
  'subscription.pdfsUsed': 'ব্যবহৃত পিডিএফ',
  'subscription.expiryWarning': '{days} দিনে মেয়াদ শেষ হবে। শীঘ্রই নবায়ন করুন।',
  'subscription.expiryWarningOne': '১ দিনে মেয়াদ শেষ হবে। শীঘ্রই নবায়ন করুন।',
  'subscription.viewUpgrade': 'প্ল্যান দেখুন / আপগ্রেড করুন',
  'subscription.cancelAction': 'সাবস্ক্রিপশন বাতিল করুন',
  'subscription.cancelling': 'বাতিল করা হচ্ছে…',
  'subscription.cancelFailed': 'বাতিল করা যায়নি। আবার চেষ্টা করুন।',
  'subscription.confirmTitle': 'সাবস্ক্রিপশন বাতিল করবেন?',
  'subscription.confirmBody':
    'বর্তমান মেয়াদ শেষ না হওয়া পর্যন্ত আপনি ব্যবহার করতে পারবেন। আপনার কাছ থেকে আর কোনো চার্জ নেওয়া হবে না।',
  'subscription.confirmYes': 'সাবস্ক্রিপশন বাতিল করুন',
  'subscription.confirmNo': 'রেখে দিন',
  'subscription.limitsTitle': 'আপনার প্ল্যানের সীমা',
  'subscription.maxWritten': 'প্রতি পিডিএফে সর্বোচ্চ সৃজনশীল প্রশ্ন:',
  'subscription.maxMcq': 'প্রতি পিডিএফে সর্বোচ্চ নৈর্ব্যক্তিক প্রশ্ন:',
  'subscription.pdfsRemaining': 'এই মাসে অবশিষ্ট পিডিএফ:',

  // ─── Dashboard: payments tab ───────────────────────────────────────────────
  'payments.signInPrompt': 'আপনার পেমেন্ট দেখতে সাইন ইন করুন।',
  'payments.emptyTitle': 'এখনো কোনো পেমেন্ট নেই',
  'payments.emptyBody': 'আপগ্রেড করার পর আপনার ক্রয় এখানে দেখা যাবে।',
  'payments.viewPlans': 'প্ল্যান দেখুন',

  // ─── Pricing ───────────────────────────────────────────────────────────────
  'pricing.badge': 'সাবস্ক্রিপশন প্ল্যান',
  'pricing.title': 'আপনার প্ল্যান বাছুন',
  'pricing.subtitle':
    'এআই দিয়ে এসএসসি ও এইচএসসি প্রশ্নপত্র তৈরি করুন। সব প্ল্যানেই বাংলা ও ইংরেজি সাপোর্ট রয়েছে।',
  'pricing.perMonth': 'প্রতি মাসে',
  'pricing.days14': '১৪ দিন',
  'pricing.contactUs': 'যোগাযোগ করুন',
  'pricing.custom': 'কাস্টম',
  'pricing.badgeFree': '১৪ দিন ফ্রি',
  'pricing.badgeBest': 'সেরা মূল্য',
  'pricing.planFreeTrial': 'ফ্রি ট্রায়াল',
  'pricing.planProTrial': 'প্রো ট্রায়াল',
  'pricing.planPro': 'প্রো',
  'pricing.planInstitution': 'প্রতিষ্ঠান',
  'pricing.pdfsFree': '১টি পিডিএফ',
  'pricing.pdfsProTrial': 'মাসে ২০টি পিডিএফ',
  'pricing.pdfsPro': 'মাসে ৪০টি পিডিএফ',
  'pricing.pdfsInstitution': 'কাস্টম পরিমাণ',
  'pricing.ctaFreeTrial': 'ফ্রি ট্রায়াল শুরু করুন',
  'pricing.ctaProTrial': 'প্রো ট্রায়াল নিন',
  'pricing.ctaPro': 'প্রো নিন',
  'pricing.ctaInstitution': 'ডেভেলপারের সাথে যোগাযোগ',
  'pricing.featPdfsPerTrial': 'ট্রায়ালে ১টি পিডিএফ',
  'pricing.featPdfsPerMonth': 'মাসে {count}টি পিডিএফ',
  'pricing.featMaxWritten': 'প্রতি পিডিএফে সর্বোচ্চ {count}টি সৃজনশীল প্রশ্ন',
  'pricing.featMaxMcq': 'প্রতি পিডিএফে সর্বোচ্চ {count}টি নৈর্ব্যক্তিক প্রশ্ন',
  'pricing.featAllSubjects': 'সব বিষয় ও অধ্যায়',
  'pricing.featPdfDownload': 'পিডিএফ ডাউনলোড',
  'pricing.featPdfImageDownload': 'পিডিএফ ও ছবি ডাউনলোড',
  'pricing.featHistory': 'প্রশ্নের ইতিহাস',
  'pricing.featPriority': 'অগ্রাধিকারভিত্তিক এআই জেনারেশন',
  'pricing.featCustomVolume': 'কাস্টম পিডিএফ পরিমাণ',
  'pricing.featCustomLimits': 'কাস্টম প্রশ্ন সীমা',
  'pricing.featMultiUser': 'একাধিক ব্যবহারকারীর প্রবেশাধিকার',
  'pricing.featSupport': 'ডেডিকেটেড সাপোর্ট',
  'pricing.featBranding': 'কাস্টম ব্র্যান্ডিং',
  'pricing.featInvoice': 'ইনভয়েস বিলিং',
  'pricing.payFailed': 'পেমেন্ট ব্যর্থ হয়েছে। আবার চেষ্টা করুন।',

  // ─── Payment result ────────────────────────────────────────────────────────
  'payment.successTitle': 'পেমেন্ট সফল হয়েছে!',
  'payment.successBody': 'আপনার সাবস্ক্রিপশন এখন সক্রিয়। এখনই প্রশ্নপত্র তৈরি করা শুরু করুন।',
  'payment.plan': 'প্ল্যান',
  'payment.pdfsPerMonth': 'মাসিক পিডিএফ',
  'payment.expires': 'মেয়াদ শেষ',
  'payment.activating': 'সাবস্ক্রিপশন সক্রিয় করা হচ্ছে…',
  'payment.tranId': 'ট্রানজেকশন আইডি: {id}',
  'payment.goToDashboard': 'ড্যাশবোর্ডে যান',
  'payment.receipt': 'আপনার নিবন্ধিত ইমেইলে একটি রসিদ পাঠানো হয়েছে।',
  'payment.failTitle': 'পেমেন্ট ব্যর্থ হয়েছে',
  'payment.failBody': 'আপনার পেমেন্ট সম্পন্ন করা যায়নি। কোনো চার্জ নেওয়া হয়নি।',
  'payment.tryAgain': 'আবার চেষ্টা করুন',
  'payment.backToDashboard': 'ড্যাশবোর্ডে ফিরে যান',
  'payment.cancelTitle': 'পেমেন্ট বাতিল হয়েছে',
  'payment.cancelBody': 'আপনি পেমেন্ট বাতিল করেছেন। আপনার বর্তমান প্ল্যান অপরিবর্তিত রয়েছে।',
  'payment.viewPlans': 'প্ল্যান দেখুন',

  // ─── Wizard: chrome ────────────────────────────────────────────────────────
  'wizard.backToOverview': 'সংক্ষিপ্ত বিবরণে ফিরে যান',
  'wizard.stepClass': 'স্তর নির্বাচন',
  'wizard.stepVersion': 'ভার্সন',
  'wizard.stepDepartment': 'বিভাগ',
  'wizard.stepSubject': 'বিষয় নির্বাচন',
  'wizard.stepPaper': 'পত্র নির্বাচন',
  'wizard.stepChapter': 'অধ্যায় নির্বাচন',
  'wizard.stepExamType': 'প্রশ্নের ধরন',
  'wizard.stepQuestions': 'প্রশ্ন',
  'wizard.stepReview': 'পর্যালোচনা',
  'wizard.stepGenerate': 'তৈরি করুন',

  // ─── Wizard: class ─────────────────────────────────────────────────────────
  'wizard.classTitle': 'স্তর নির্বাচন করুন',
  'wizard.classSubtitle': 'কোন পরীক্ষার জন্য প্রশ্ন তৈরি করতে চান তা বাছুন।',
  'wizard.classSscFull': 'মাধ্যমিক স্কুল সার্টিফিকেট',
  'wizard.classHscFull': 'উচ্চ মাধ্যমিক সার্টিফিকেট',
  'wizard.classSscRange': 'নবম – দশম শ্রেণি',
  'wizard.classHscRange': 'একাদশ – দ্বাদশ শ্রেণি',

  // ─── Wizard: version ───────────────────────────────────────────────────────
  'wizard.versionTitle': 'ভার্সন নির্বাচন করুন',
  'wizard.versionSubtitle': 'কোন মাধ্যমে প্রশ্ন তৈরি হবে তা বাছুন।',
  'wizard.versionBangla': 'বাংলা ভার্সন',
  'wizard.versionBanglaSub': 'বাংলা মাধ্যম',
  'wizard.versionEnglish': 'English Version',
  'wizard.versionEnglishSub': 'ইংরেজি মাধ্যম',

  // ─── Wizard: department ────────────────────────────────────────────────────
  'wizard.departmentTitle': 'বিভাগ নির্বাচন করুন',
  'wizard.departmentSubtitle': '{class} শ্রেণির জন্য আপনার বিভাগ বাছুন — {level}।',
  'wizard.departmentSkipping': 'বিভাগ নির্বাচন এড়িয়ে যাওয়া হচ্ছে…',
  'wizard.levelSsc': 'এসএসসি (মাধ্যমিক)',
  'wizard.levelHsc': 'এইচএসসি (উচ্চ মাধ্যমিক)',

  // ─── Wizard: subject / paper / chapter ─────────────────────────────────────
  'wizard.subjectTitle': 'বিষয় নির্বাচন করুন',
  'wizard.subjectSubtitle': 'কোন বিষয়ের প্রশ্ন তৈরি করতে চান তা বাছুন।',
  'wizard.paperTitle': 'পত্র নির্বাচন করুন',
  'wizard.paperSubtitle': '{subject}-এর জন্য পত্র বাছুন।',
  'wizard.paperChapters': '{count}টি অধ্যায়',
  'wizard.chapterTitle': 'অধ্যায় নির্বাচন করুন',
  'wizard.chapterSubtitle': '{source} থেকে এক বা একাধিক অধ্যায় বাছুন। একাধিক নির্বাচন করা যাবে।',
  'wizard.chapterPlaceholder': 'অধ্যায় নির্বাচন করুন…',
  'wizard.chapterSelected': '{count}টি অধ্যায় নির্বাচিত',
  'wizard.chapterSelectedOne': '১টি অধ্যায় নির্বাচিত',
  'wizard.chapterSearch': 'অধ্যায় খুঁজুন…',
  'wizard.chapterNone': 'কোনো অধ্যায় পাওয়া যায়নি',
  'wizard.chapterSelectedCount': '{count}টি নির্বাচিত',

  // ─── Wizard: exam type ─────────────────────────────────────────────────────
  'wizard.examTypeTitle': 'প্রশ্নের ধরন নির্বাচন করুন',
  'wizard.examTypeSubtitle': 'আপনি কী ধরনের প্রশ্ন তৈরি করতে চান?',
  'wizard.examTypeWrittenDesc': 'ক খ গ ঘ অংশসহ সৃজনশীল প্রশ্ন',
  'wizard.examTypeMcqDesc': '৪টি অপশনসহ বহুনির্বাচনি প্রশ্ন',

  // ─── Wizard: questions & duration ──────────────────────────────────────────
  'wizard.questionsTitle': 'প্রশ্ন ও সময়',
  'wizard.questionsSubtitle': 'প্রশ্নের সংখ্যা, নির্ধারিত সময় এবং কঠিনতার মাত্রা ঠিক করুন।',
  'wizard.questionsNumberLabel': 'প্রশ্নের সংখ্যা',
  'wizard.questionsWrittenHint': 'সৃজনশীল প্রশ্নে প্রতিটি প্রশ্নে ক, খ, গ, ঘ অংশ থাকে।',
  'wizard.questionsMcqHint': 'প্রতিটি নৈর্ব্যক্তিক প্রশ্নের মান ১ নম্বর।',
  'wizard.questionsLimitWritten':
    'আপনার প্ল্যানে প্রতি পিডিএফে সর্বোচ্চ {max}টি সৃজনশীল প্রশ্ন তৈরি করা যাবে।',
  'wizard.questionsLimitMcq':
    'আপনার প্ল্যানে প্রতি পিডিএফে সর্বোচ্চ {max}টি নৈর্ব্যক্তিক প্রশ্ন তৈরি করা যাবে।',
  'wizard.durationLabel': 'সময়',
  'wizard.durationHint': '+/− বোতাম ব্যবহার করুন অথবা সরাসরি লিখুন।',
  'wizard.difficultyLabel': 'কঠিনতার মাত্রা',
  'wizard.difficultyEasy': 'সহজ',
  'wizard.difficultyMedium': 'মাঝারি',
  'wizard.difficultyHard': 'কঠিন',
  'wizard.difficultyMixed': 'মিশ্র',
  'wizard.difficultyEasyDesc': 'মৌলিক স্মরণ ও সংজ্ঞা',
  'wizard.difficultyMediumDesc': 'প্রয়োগ ও ব্যাখ্যা',
  'wizard.difficultyHardDesc': 'বিশ্লেষণ ও উচ্চতর চিন্তন',
  'wizard.difficultyMixedDesc': 'সব মাত্রার মিশ্রণ',

  // ─── Wizard: review ────────────────────────────────────────────────────────
  'wizard.reviewTitle': 'আপনার নির্বাচন পর্যালোচনা করুন',
  'wizard.reviewSubtitle': 'প্রশ্ন তৈরির আগে সবকিছু ঠিক আছে কিনা নিশ্চিত করুন।',
  'wizard.reviewNext': 'সব ঠিক আছে, চালিয়ে যান',
  'wizard.reviewCardTitle': 'আপনার নির্বাচন পর্যালোচনা',
  'wizard.reviewCardSubtitle': 'সব ঠিক আছে? প্রস্তুত হলে তৈরি করুন।',
  'wizard.reviewClass': 'শ্রেণি',
  'wizard.reviewDepartment': 'বিভাগ',
  'wizard.reviewSubject': 'বিষয়',
  'wizard.reviewPaper': 'পত্র',
  'wizard.reviewChapters': 'অধ্যায়',
  'wizard.reviewExamType': 'প্রশ্নের ধরন',
  'wizard.reviewQuestions': 'প্রশ্ন',
  'wizard.reviewDuration': 'সময়',
  'wizard.reviewTotalMarks': 'মোট নম্বর',
  'wizard.reviewVersion': 'ভার্সন',
  'wizard.reviewClassSsc': 'এসএসসি (নবম–দশম শ্রেণি)',
  'wizard.reviewClassHsc': 'এইচএসসি (একাদশ–দ্বাদশ শ্রেণি)',

  // ─── Wizard: generate ──────────────────────────────────────────────────────
  'wizard.generateTitle': 'তৈরির জন্য প্রস্তুত!',
  'wizard.generateSubtitle': 'আপনার নির্বাচিত সেটিংস অনুযায়ী এআই প্রশ্নপত্র তৈরি করবে।',
  'wizard.generateSummary': 'সারসংক্ষেপ',
  'wizard.generateCta': 'প্রশ্ন তৈরি করুন',
  'wizard.generating': 'এআই দিয়ে তৈরি হচ্ছে…',
  'wizard.generateNote': 'এআই বাংলাদেশ {level} বোর্ড পরীক্ষার কারিকুলাম অনুযায়ী প্রশ্ন তৈরি করবে।',
  'wizard.generateNoTemplate': 'নির্বাচিত বিষয় ও প্রশ্নের ধরনের জন্য কোনো টেমপ্লেট পাওয়া যায়নি।',
  'wizard.generateFailed': 'প্রশ্ন তৈরি করা যায়নি। আবার চেষ্টা করুন।',

  // ─── Generated questions viewer ────────────────────────────────────────────
  'questions.count': '{count}টি প্রশ্ন',
  'questions.aiGenerated': 'এআই দিয়ে তৈরি',
  'questions.png': 'পিএনজি',
  'questions.downloadPdf': 'পিডিএফ ডাউনলোড',
  'questions.banner':
    '{chapters} অধ্যায়ের জন্য {count}টি প্রশ্ন তৈরি হয়েছে। পিডিএফ বা পিএনজি হিসেবে ডাউনলোড করুন।',
  'questions.answerKey': 'উত্তরপত্র',
  'questions.questionNumber': 'প্রশ্ন {number}',
  'questions.noPaper': 'কোনো প্রশ্নপত্র পাওয়া যায়নি। ফিরে গিয়ে একটি তৈরি করুন।',
};
